import { useState, useEffect, useRef } from 'react';
import { MANAGERS, EVENT_TYPES, VENUES } from '../lib/constants';
import { settingsService } from '../services/api';

const CACHE_KEY_EVENT_TYPES = 'app_settings_event_types';
const CACHE_KEY_VENUES = 'app_settings_venues';
const MAX_RETRIES = 3;
const RETRY_DELAYS = [2000, 4000, 8000]; // exponential backoff

/**
 * Try to get cached settings from sessionStorage
 */
function getCached(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    // ignore parse errors
  }
  return null;
}

function setCache(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // ignore storage errors
  }
}

/**
 * Hook to get app settings (managers, event types, venues) from backend settings API.
 * 
 * Priority order:
 * 1. sessionStorage cache (instant, from previous successful fetch)
 * 2. API fetch (with retry on failure for slow networks)
 * 3. Hardcoded constants (only as last resort after all retries exhausted)
 * 
 * This prevents showing hardcoded fallbacks when the network is slow —
 * settings stay null until the API responds or all retries fail.
 */
export function useAppSettings() {
  const [managers, setManagers] = useState(MANAGERS);
  // Initialize from cache first, then null — never from hardcoded constants
  const [eventTypes, setEventTypes] = useState(() => getCached(CACHE_KEY_EVENT_TYPES));
  const [venues, setVenues] = useState(() => getCached(CACHE_KEY_VENUES));
  const [loading, setLoading] = useState(true);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadSettings = async (isRetry = false) => {
      // Skip API call if user is not authenticated yet — avoids 401 on live
      const token = localStorage.getItem('token');
      if (!token) {
        setEventTypes(EVENT_TYPES);
        setVenues(VENUES);
        setLoading(false);
        return;
      }

      try {
        const [evts, vens] = await Promise.all([
          settingsService.get('event_types'),
          settingsService.get('venues')
        ]);

        if (cancelled) return;

        // Reset retry count on success
        retryCountRef.current = 0;

        if (evts && evts.list && evts.list.length > 0) {
          setEventTypes(evts.list);
          setCache(CACHE_KEY_EVENT_TYPES, evts.list);
        } else {
          // API returned empty — use hardcoded constants as fallback
          setEventTypes(EVENT_TYPES);
        }

        if (vens && vens.list && vens.list.length > 0) {
          setVenues(vens.list);
          setCache(CACHE_KEY_VENUES, vens.list);
        } else {
          // API returned empty — use hardcoded constants as fallback
          setVenues(VENUES);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading app settings:', err);

        // Retry with exponential backoff for slow networks
        if (retryCountRef.current < MAX_RETRIES) {
          const delay = RETRY_DELAYS[retryCountRef.current] || 8000;
          retryCountRef.current += 1;
          console.log(`Retrying app settings fetch (attempt ${retryCountRef.current}/${MAX_RETRIES}) in ${delay}ms...`);
          retryTimerRef.current = setTimeout(() => {
            if (!cancelled) loadSettings(true);
          }, delay);
          return; // Don't set loading=false yet, still retrying
        }

        // All retries exhausted — fall back to hardcoded constants
        console.warn('All retries exhausted for app settings, using hardcoded fallbacks');
        if (!eventTypes || eventTypes.length === 0) {
          setEventTypes(EVENT_TYPES);
        }
        if (!venues || venues.length === 0) {
          setVenues(VENUES);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSettings();

    const handleUpdate = () => {
      retryCountRef.current = 0; // Reset retries on manual update
      loadSettings();
    };

    // Re-fetch settings after login (token becomes available)
    const handleLogin = () => {
      retryCountRef.current = 0;
      loadSettings();
    };

    window.addEventListener('app_settings_updated', handleUpdate);
    window.addEventListener('auth:login', handleLogin);
    return () => {
      cancelled = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      window.removeEventListener('app_settings_updated', handleUpdate);
      window.removeEventListener('auth:login', handleLogin);
    };
  }, []);

  return { managers, eventTypes, venues, loading };
}
